import { GridColDef } from "@mui/x-data-grid";
import ExternalLinkAnchor from "../../common/ExternalLinkAnchor";
import FormattedCountryCell from "./FormattedCountryCell";
import { Chip, Stack } from "@mui/material";
import { startCase } from "lodash";

const PAYMENT_PLAN_LEVELS = [
  "free",
  "starter",
  "standard",
  "complete",
  "enterprise",
  "legacy",
];

const dateFormatter = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString("en-GB");
};

const columns: Array<GridColDef> = [
  {
    display: "flex",
    field: "displayName",
    headerName: "Client",
    minWidth: 210,
    flex: 1,
  },
  {
    display: "flex",
    field: "ownerEmail",
    headerName: "Owner",
    minWidth: 300,
    flex: 1,
  },
  {
    display: "flex",
    field: "createdAt",
    headerName: "Date Created",
    minWidth: 120,
    flex: 1,
    type: "date",
    valueGetter: (_, row) => new Date(row.createdAt),
    renderCell: (params) => dateFormatter(params.row.createdAt),
  },
  {
    display: "flex",
    field: "lastUpdated",
    headerName: "Last Updated",
    minWidth: 120,
    flex: 1,
    type: "date",
    valueGetter: (_, row) => new Date(row.lastUpdated),
    renderCell: (params) => dateFormatter(params.row.lastUpdated),
  },
  {
    display: "flex",
    field: "products",
    headerName: "Products",
    minWidth: 160,
    flex: 1,
    type: "string",
    valueGetter: (_, row) => row.products,
    renderCell: (params) => {
      const productList = params.row.products as string[] | undefined;

      if (!productList?.length) return "-";

      return (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            flexWrap: "wrap",
            paddingTop: 1,
            paddingBottom: 1,
            width: "100%",
          }}
        >
          {productList.map((name) => (
            <Chip key={name} label={name} size="small" sx={{ margin: 0.25 }} />
          ))}
        </Stack>
      );
    },
  },
  {
    display: "flex",
    field: "paymentPlan",
    headerName: "Payment Plan",
    minWidth: 130,
    flex: 1,
    type: "singleSelect",
    valueOptions: PAYMENT_PLAN_LEVELS.map((level) => ({
      value: level,
      label: startCase(level),
    })),
    renderCell: (params) =>
      params.value ? startCase(params.value as string) : "-",
  },
  {
    display: "flex",
    field: "public",
    headerName: "Dashboard",
    minWidth: 70,
    flex: 1,
    type: "boolean",
    valueGetter: (_, row) => row.public.isPublic,
    renderCell: (params) => {
      return (
        <ExternalLinkAnchor
          url={`https://dashboard.earthly.org/${params.row.id}`}
          dataTestid="client-details-v3-profile-url-anchor"
        />
      );
    },
  },
  {
    display: "flex",
    field: "street",
    headerName: "Address",
    minWidth: 200,
    flex: 1,
  },
  {
    display: "flex",
    field: "city",
    headerName: "City",
    minWidth: 155,
    flex: 1,
  },
  {
    display: "flex",
    field: "zipCode",
    headerName: "Post Code",
    minWidth: 90,
    flex: 1,
  },
  {
    display: "flex",
    field: "country",
    headerName: "Country",
    minWidth: 210,
    flex: 1,
    valueGetter: (_, row) => {
      if (!row.country) {
        return null;
      }
      return row.country.name;
    },
    renderCell: (params) => (
      <FormattedCountryCell country={params.row.country} />
    ),
  },
];

export default columns;
